import PropTypes from 'prop-types';
import React, { Component, useEffect, useRef } from 'react';
import prepareEmbeddedHomePage from './helpViewUtils';

import embeddedHelpCssOverrides from './embeddedHelpCssOverrides.scss?as-css-string';

// If a help doc fails to load after 20s show an error page:
const LOADING_TIMEOUT_MS = 20000;

function makeHelpPageStyleTag(doc) {
  const styleTag = doc.createElement('style');
  styleTag.textContent = embeddedHelpCssOverrides;
  return styleTag;
}

// Using Component style to make a custom error boundary action (`componentDidCatch`).
export class HelpViewIframeErrorBoundary extends Component {
  static displayName = 'HelpViewIframeErrorBoundary';

  static propTypes = {
    children: PropTypes.node.isRequired,
    onError: PropTypes.func.isRequired,
  };

  state = {
    hasError: false,
  };

  componentDidCatch(error, info) {
    this.setState({ hasError: true });
    // eslint-disable-next-line no-console
    console.error('HelpView ErrorBoundary caught: ', {
      error,
      errorStack: error?.stack,
      info,
    });
    this.props.onError();
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

HelpViewIframeErrorBoundary.displayName = 'HelpViewIframeErrorBoundary';
HelpViewIframeErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onError: PropTypes.func.isRequired,
};

export default function HelpViewIframe({
  iframeWindowHrefRef,
  iframeSrc,
  isLoading,
  setHasLoadingError,
  setIsLoading,
}) {
  const loadingErrorTimeoutRef = useRef();

  // Mark as loading on mount:
  useEffect(() => {
    setIsLoading(true);
  }, [setIsLoading]);

  useEffect(() => {
    // Force a timeout after which we can show an error as sometimes browsers don't catch errors on iframe well:
    clearTimeout(loadingErrorTimeoutRef.current);
    if (isLoading) {
      setHasLoadingError(false);
      loadingErrorTimeoutRef.current = setTimeout(() => {
        setHasLoadingError(true);
        setIsLoading(false);
      }, LOADING_TIMEOUT_MS);
    }
    // Trigger on unmount (so, on close);
    return () => {
      clearTimeout(loadingErrorTimeoutRef.current);
    };
  }, [isLoading, loadingErrorTimeoutRef, setIsLoading, setHasLoadingError]);

  /**
   * The innerRef / iframeWindowHrefRef divergence is due to ErrorBoundary weirdness in React.
   *
   * Problem / Weirdness:
   *    In some cases tested (e.g. CSP and X-FRAME-ORIGIN errors) React is showing the ErrorBoundary
   *    above the _ref definition_, which prevents custom errors specific to iframe-load issues
   *    since iframeWindowHrefRef is needed in HelpView.jsx.
   *
   * Workaround:
   *   - The iframe's main ref is an "innerRef", so its nearest ErrorBoundary ancestor is HelpViewIframeErrorBoundary
   *   - After load, the outer ref (`iframeWindowHrefRef`) that is needed in HelpView.jsx is updated.
   * in a lot of cases. Using an innerRef and onLoad to get around that and
   * ensure use of the inner HelpViewIframeErrorBoundary.
   */
  const innerRef = useRef();
  return (
    <iframe
      ref={element => {
        innerRef.current = element;
        // eslint-disable-next-line no-param-reassign
        iframeWindowHrefRef.current = null;
      }}
      width="100%"
      height="100%"
      src={iframeSrc}
      frameBorder="0"
      onError={function onIframeError() {
        setHasLoadingError(true);
        setIsLoading(false);
      }}
      onLoad={function onIframeLoad() {
        const { contentDocument, contentWindow } = innerRef?.current ?? {};
        if (!contentDocument) {
          setHasLoadingError(true);
          setIsLoading(false);
          return;
        }

        // Update iframeWindowHrefRef for outer components (e.g. "open in new tab") to be able to use it:
        // eslint-disable-next-line no-param-reassign
        iframeWindowHrefRef.current = contentWindow.location.href;

        // Override some styling so that the docs look cleaner embedded
        const styleTag = makeHelpPageStyleTag(contentDocument);
        const addStyleTag = () => {
          contentDocument.head.insertAdjacentElement('afterbegin', styleTag);
          // In some cases either the MadCap contents or the browser paint gets confused and leaves
          // a large top margin. The margin disappears on resize, so hacking through that :/
          contentWindow.dispatchEvent(new CustomEvent('resize'));
        };
        addStyleTag();

        // COR-75360 Remove unwanted elements from the page before loading
        if (iframeWindowHrefRef.current.includes('xmHome')) {
          prepareEmbeddedHomePage(contentDocument);
        }

        /**
         * Help pages use a framework that basically loads the page, throws away the content, and then reloads.
         * The below code is basically to try to minimize the UI jankiness when that happens.
         * Essentially we delay rendering slightly, and
         */
        let isUnloaded = false;
        function preventDisplay() {
          isUnloaded = true;
          setIsLoading(true);
          contentWindow.removeEventListener('beforeunload', preventDisplay);
        }
        contentWindow.addEventListener('beforeunload', preventDisplay);

        // Fighting against MadCap loading, unloading, and stripping out tags is a pain... ideally it
        // would be amazing to tell the Help page to natively load styling that works for this.
        setTimeout(addStyleTag, 125);
        setTimeout(() => {
          if (!isUnloaded) {
            addStyleTag();
            setIsLoading(false);
          }
        }, 250);
        setHasLoadingError(false);
      }}
      title="helpDialog"
      data-testid="HelpView_iframe"
      /**
       * Note to future maintainer:
       *   `hidden` can prevent auto-scrolling to anchor content in Chrome, likely other browsers too.
       *   Example is TETH-15142, where opening on User Profile Groups tab requires scrolling. As such,
       *   I do not recommend adding `hidden={isLoading}`, instead cover the iframe with another element.
       */
    />
  );
}

HelpViewIframe.displayName = 'HelpViewIframe';
HelpViewIframe.propTypes = {
  iframeWindowHrefRef: PropTypes.object.isRequired,
  iframeSrc: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setHasLoadingError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
};
